import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Dimensions } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { getCommunityResourceIcon } from '@/constants/residents/communityResourceIcons';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { CommunityResource } from '@/types/residents/communityResource';

const SCREEN_WIDTH = Dimensions.get('window').width;

interface ResourceCardProps {
  resource?: CommunityResource;
  isMoreResourcesCard?: boolean;
  isFullWidth?: boolean;
  isInMoreResourcesScreen?: boolean;
  onPress: (resource?: CommunityResource) => void;
}

export const ResourceCard: React.FC<ResourceCardProps> = ({ 
  resource, 
  isMoreResourcesCard = false,
  isFullWidth = false,
  isInMoreResourcesScreen = false,
  onPress 
}) => {
  // Get icon name - support both new and legacy formats
  const getIconName = (): string => {
    if (isMoreResourcesCard) return 'apps-outline';
    if (resource?.icon) return getCommunityResourceIcon(resource.icon);
    if (resource?.iconName) return getCommunityResourceIcon(resource.iconName);
    return 'grid-outline';
  };

  const iconName = getIconName();
  
  // Support both new (name) and legacy (title) field names
  const title = isMoreResourcesCard 
    ? 'More resources' 
    : resource?.name || resource?.title || '';
  const description = isMoreResourcesCard 
    ? 'See what else is out there' 
    : resource?.description || '';

  return (
    <TouchableOpacity 
      style={[
        styles.card, 
        isFullWidth && styles.fullWidthCard,
        isInMoreResourcesScreen && styles.moreResourcesScreenCard
      ]} 
      onPress={() => onPress(resource)}
      accessibilityRole="button"
      accessibilityLabel={`${title}: ${description}`}
    >
      <View style={styles.icon}>
        <Icon 
          name={iconName} 
          size={24} 
          color={MARKETPLACE_COLORS.accentBlueSolid} 
        />
      </View>
      <Text style={styles.title} numberOfLines={1}>
        {title}
      </Text>
      <Text style={styles.description} numberOfLines={2}>
        {description}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    // Calculate width for 2-column grid with 16px gap
    // Inside MobileScreenLayout with 20px padding on each side
    width: (SCREEN_WIDTH - 40 - 16) / 2,
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    padding: 16,
  },
  fullWidthCard: {
    width: '100%',
    alignItems: 'flex-start',
  },
  moreResourcesScreenCard: {
    height: SCREEN_WIDTH * 0.4,
  },
  icon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: MARKETPLACE_COLORS.white10Percent,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    color: MARKETPLACE_COLORS.textPrimary,
    marginBottom: 2,
  },
  description: {
    fontSize: 13,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textMuted,
  },
});
